import type { ItemState } from './session-state'

/**
 * 团队成员与临时子 Agent —— 这个模块存在的唯一理由是**它们不是一种东西**。
 *
 * # 为什么要用类型把它们隔开
 *
 * 设计稿把这一条单独立了一节，并标成「最容易实现错」。原话：
 *
 * > 用户说「多开几个一起做」，如果实现成「往团队里加人」，那么**一次临时并行
 * > 会永久改掉团队编制**，而且这些人还会进 Agent 库。
 *
 * 这类错误的形状很特别：**它跑起来完全正常**。开三个子 Agent，界面显示四人
 * 团队，活也干完了——用户下一次开会话时才发现团队里多了三个他没请过的人，
 * 而那时已经没人记得是哪一次并行留下的。
 *
 * 防它的办法不是记得，是让「把子 Agent 塞进团队名单」这件事**类型上做不到**：
 * 两个不同的类型，`teamSize` 只数一种，`mentionable` 只列一种。
 *
 * ## 12 条差异里，代码要守住的是这几条
 *
 * | | 团队成员 | 临时子 Agent |
 * |---|---|---|
 * | 算进人数吗 | 算 | **不算** |
 * | 进 Agent 库吗 | 进 | **不进** |
 * | @ 能点到吗 | 能 | **不能** |
 * | 谁对结果负责 | 自己 | **父 Agent** |
 * | 证据挂在哪 | 自己的工作项 | **父工作项** |
 * | 活多久 | 一直在 | 终态自动关闭 |
 */

/**
 * 会话里的一个真人。
 *
 * ------------------------------------------------------------------
 * # 为什么人和 Agent 是两个类型，不是同一个加一个 `isHuman`
 *
 * 因为**它们能被做的事不一样**，而合成一个类型之后那件事就只能靠
 * `if` 去守：
 *
 * | | Agent | 人 |
 * |---|---|---|
 * | @ 他会怎样 | 他回话、可能开一件活 | **只是通知他来看，不产生任何执行** |
 * | 有模型吗 | 有（第三层灰字） | **没有**，人不是模型驱动的 |
 * | 有角色吗 | 有职责（frontend/backend…） | 有**账户角色**（老板/干活的/看看就好） |
 *
 * 合成一个的话，`modelId` 对人恒为 null——而 null 在渲染时与
 * 「这个 Agent 还没选模型」长得一模一样。
 *
 * # canLetAIAct 是服务端算好的，前端不许自己按 role 再推一遍
 *
 * 那就是同一条规则的第二份实现，而**放宽的那一份不会有任何东西会红**。
 * 服务端那份在 team.Can(role, CapLetAIAct)。
 * ------------------------------------------------------------------
 */
export interface HumanMember {
  readonly kind: 'human'
  /** users.id。@ 与移出都按它。 */
  readonly id: string
  readonly name: string
  /** 账户角色。**认不出来时是 null**——不给默认档，界面据此说「认不出他这一档」。 */
  readonly role: 'owner' | 'operator' | 'viewer' | null
  /** 他能不能 @ 一位 AI 去干活。服务端算好的结论，照着渲染就行。 */
  readonly canLetAIAct: boolean
  readonly joinedAt: string
}

/** 团队里的一位，可能是 Agent 也可能是人。 */
export type TeamSeat = TeamMember | HumanMember

/** 团队成员。**只能是 AgentProfile，永远不是 ModelProfile。** */
export interface TeamMember {
  readonly kind: 'agent'
  readonly id: string
  /** 用户起的名字。空着由服务端按职责自动生成，见 `resolveDisplayName`。 */
  readonly name: string
  readonly nameSource: 'user' | 'auto'
  /** 一句话说它管什么。名字缺失时它是唯一合法的替代。 */
  readonly role: string
  /** 模型是**配置**不是身份。渲染时它在第三层，灰色小字。 */
  readonly modelId: string | null
  readonly state: ItemState | null
  readonly scope: 'account' | 'project'
}

/**
 * 临时子 Agent。**不是 AgentProfile**——它是某位主 Agent 为当前任务临时开的
 * 执行单元，终态自动关闭且不可复用。
 */
export interface SubAgentRun {
  readonly kind: 'subrun'
  readonly id: string
  /** 实际执行这次临时运行的 AgentProfile。用于显示 Agent 名，不拿模型冒充身份。 */
  readonly agentProfileId?: string | null
  readonly name: string
  readonly nameSource: 'user' | 'auto'
  /** 它负责的那件事。名字为空时由它生成，**绝不由 modelId 派生**。 */
  readonly responsibility: string
  readonly modelId: string | null
  /** 谁开的。子 Agent 只提供事实，结论由父 Agent 给。 */
  readonly parentAgentId: string
  /** 它的产出挂在这个工作项下，不单独成一条历史。 */
  readonly workItemId: string
  readonly state: ItemState | null
  /** 没有事件时是 null。**前端不得补 0 或补动画。** */
  readonly progress: number | null
  readonly elapsedMs: number | null
}

/**
 * 团队人数。**只数 TeamMember。**
 *
 * 签名上就收不下 SubAgentRun——这不是防御性编程，是把设计稿那条规则
 * 交给类型系统：想把子 Agent 数进来，得先改这个签名，而改签名是一件
 * 有人会在评审里看见的事。悄悄多一个人不行。
 */
export function teamSize(members: readonly TeamMember[]): number {
  return members.length
}

/**
 * `@` 能点到谁。
 *
 * 设计稿：「@ 只提及当前团队成员，不新增、不移除 roster 成员」，
 * 而子 Agent「不能」被 @ 到。所以这里同样只收 TeamMember。
 */
export function mentionable(members: readonly TeamMember[]): readonly TeamMember[] {
  return members
}

/**
 * 这个人能不能 @ 一位 AI 去干活。
 *
 * **只读服务端算好的那个字段**，不按 role 再推一遍——两份判据分家的那天，
 * 放宽的那一份不会有任何东西会红。角色认不出来（role 为 null）时按不能办。
 */
export function canDispatch(member: HumanMember): boolean {
  return member.canLetAIAct
}

/**
 * 队伍总人数：Agent 与真人一起数。
 *
 * 与 `teamSize` 分开是因为那一个的签名**刻意只收 TeamMember**（子 Agent
 * 不许被数进来）。把人塞进那个签名等于把那道门也一起拆了。
 */
export function seatCount(seats: readonly TeamSeat[]): number {
  return seats.length
}

/** 把一份混着的名单拆成两半，渲染时分组用。 */
export function splitSeats(seats: readonly TeamSeat[]): {
  readonly agents: readonly TeamMember[]
  readonly people: readonly HumanMember[]
} {
  const agents: TeamMember[] = []
  const people: HumanMember[] = []
  for (const seat of seats) {
    if (seat.kind === 'human') people.push(seat)
    else agents.push(seat)
  }
  return { agents, people }
}

/**
 * 界面上显示的名字。
 *
 * 三条硬规则，按优先级：
 *
 * 1. 有名字就用名字。
 * 2. 没名字就用**它负责的那件事** —— 这是设计稿说的「按职责自动起名」在
 *    前端这一侧唯一合法的兜底。服务端本该已经起好，这里是它没起时的下限。
 * 3. **两者都没有就返回 null，让调用方干脆不显示名字。**
 *
 * 第 3 条看着奇怪，它挡的是两件被明令禁止的事：
 *
 *   - 用 modelId 当名字。那会让界面上出现一个叫 `claude-sonnet-4.5` 的「成员」，
 *     而设计稿整节都在讲「Agent 是具名角色，模型只是它的配置」。
 *   - 显示「未命名 Agent」。验收 §C.15 与设计稿 §4 都点名禁止这个占位。
 *
 * 不显示名字，比显示一个错的名字诚实。参与者那一侧已经用过同一条：
 * 解不出是谁说的就不显示，而不是把 uuid 摆出来。
 */
export function resolveDisplayName(input: {
  readonly name?: string | null
  readonly role?: string | null
  readonly responsibility?: string | null
  readonly modelId?: string | null
}): string | null {
  const name = input.name?.trim()
  if (name) return name
  const fallback = (input.responsibility ?? input.role)?.trim()
  if (fallback) return fallback
  // 这里刻意不碰 input.modelId。它作为参数存在，是为了让类型上看得见
  // 「我拿得到它，而我选择不用」——删掉它，下一个人会以为只是忘了传。
  return null
}

/** 这个名字是不是平台起的。界面据此挂「自动命名」标，并允许改名。 */
export function isAutoNamed(entity: { readonly nameSource: 'user' | 'auto' }): boolean {
  return entity.nameSource === 'auto'
}

/**
 * 一轮子 Agent 跑完之后，哪些东西**必须没变**。
 *
 * 设计稿 S05 是这一组里最关键的一屏——它是「临时」二字唯一的证明。
 * 这个函数把那一屏的三行断言变成一个可计算的结果，于是它能被用例钉住，
 * 而不是靠人在界面上看一眼。
 */
export interface AfterSubRunsInvariant {
  readonly teamSize: number
  readonly libraryGained: number
  readonly mentionableCount: number
}

export function invariantAfterSubRuns(
  membersBefore: readonly TeamMember[],
  membersAfter: readonly TeamMember[],
  librarySizeBefore: number,
  librarySizeAfter: number,
): AfterSubRunsInvariant & { readonly unchanged: boolean } {
  const before = teamSize(membersBefore)
  const after = teamSize(membersAfter)
  const gained = librarySizeAfter - librarySizeBefore
  return {
    teamSize: after,
    libraryGained: gained,
    mentionableCount: mentionable(membersAfter).length,
    unchanged: before === after && gained === 0,
  }
}

/**
 * 停掉一个子 Agent。
 *
 * 返回新的列表，**父 Agent 与其他子 Agent 原样不动**——设计稿 S03：
 * 「停掉它不影响小柯和另外两个子 Agent」。
 *
 * 写成纯函数是为了让「不影响别人」这件事能被断言。写成就地改一个数组，
 * 「别的元素没被碰」就只能靠读代码来确认。
 */
export function cancelSubRun(runs: readonly SubAgentRun[], id: string): readonly SubAgentRun[] {
  return runs.map((run) => (run.id === id ? { ...run, state: 'closed' as ItemState } : run))
}
